import React, { Fragment } from 'react';
import { useFormik } from 'formik';
import { Checkbox, Row, Col } from 'antd';
import styled from 'styled-components';
import { Constants, Validators } from 'core';
import { Section, RequestError, SectionName, ProfilParams } from 'types';
import { path } from 'ramda';

import Input from 'components/organisms/input';
import Button from 'components/molecules/button';
import FormError from 'components/molecules/form-error';
import { useIntl } from 'components/../common/config/i18n';

type Permission = {
  read: boolean;
  write: boolean;
};

type FormikValuesWithoutName = { [key in SectionName]?: Permission };

type FormikValues = FormikValuesWithoutName & {
  name: string;
};

interface Props {
  onCreate: (values: Omit<ProfilParams, 'id'>) => void;
  sections?: Section[];
  error?: RequestError;
  status?: 'loading' | 'error' | 'success';
}

const WrapperForm = styled.header`
  margin-bottom: 20px;
`;
const WrapperInput = styled.div`
  margin-top: 20px;
`;

const UpLabel = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: bold;
`;

const radioStyle = {
  marginRight: 30,
};

const Form = ({ onCreate, sections, error, status }: Props) => {
  const { t } = useIntl();

  const InitialsValues: FormikValuesWithoutName | undefined = sections?.reduce(
    (obj, item) => Object.assign(obj, { [item.name]: name }),
    {}
  );
  const initialValues: FormikValues = { name: '', ...InitialsValues };

  const formik = useFormik({
    initialValues,
    validate: (values) => Validators.createProfil()(values),
    onSubmit: (values) => {
      const mappedValues = Object.entries(values).map((e) => ({ [e[0]]: e[1] }));
      const createProfilParams: Omit<ProfilParams, 'id'> = {
        name: values.name,
        sectionsAndPermissions: mappedValues?.slice(1)?.map((x, index) => {
          return {
            id: index + 1,
            ...((sections ? x[sections[index].name] : {}) as Omit<Permission, 'id'>),
          };
        }),
      };
      onCreate(createProfilParams);
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <WrapperForm>
        <Input
          width="270px"
          upLabel={t('form', 'NAME')}
          name="name"
          onChange={formik.handleChange}
          value={formik.values.name}
        />
        <FormError formik={formik} fieldName="name" />
        {sections?.map((section) => {
          const prop = section.name;
          return (
            <Fragment key={section.id}>
              <WrapperInput>
                <UpLabel>{t('form', section.name)}</UpLabel>
                <Checkbox.Group
                  onChange={(checkedValues) =>
                    formik.setFieldValue(prop, {
                      write: checkedValues.includes('write'),
                      read: checkedValues.includes('read'),
                    })
                  }
                >
                  <Row>
                    <Col>{section?.readLabel && <Checkbox value="read">{section?.readLabel}</Checkbox>}</Col>
                    <Col>
                      {section?.writeLabel && (
                        <Checkbox style={radioStyle} value="write">
                          {section?.writeLabel}
                        </Checkbox>
                      )}
                    </Col>
                  </Row>
                </Checkbox.Group>
              </WrapperInput>
              <FormError formik={formik} fieldName={section.name} />
            </Fragment>
          );
        })}
      </WrapperForm>
      {error && (
        <FormError
          message={
            error === Constants.ALREADY_EXISTED_PROBLEM_CODE
              ? t('form', 'FORM_PROFIL_ALLREADY_EXIST')
              : t('form', 'FORM_UNKNOWN_ERROR')
          }
        />
      )}

      <Button width="100%" borderRadius="20px" loading={status === 'loading'} htmlType="submit">
        {t('Buttons', 'CREATE_BUTTON')}
      </Button>
    </form>
  );
};
export default Form;
